import {defineStore} from 'pinia';
import {computed, ref} from 'vue';
import {useApiClient} from "@/hooks/api-hook.ts";
import {ACMI, ACMICreateRequest, ACMIUpdateRequest} from "@/api/client/models/index.ts";

export const useAcmiStore = defineStore('acmi', () => {
  const api = useApiClient();

  // State
  const acmiList = ref<ACMI[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const acmiCount = computed(() => acmiList.value.length);
  const hasAcmi = computed(() => acmiList.value.length > 0);
  const isLoading = computed(() => loading.value);
  const hasError = computed(() => error.value !== null);

  // Actions
  async function fetchAcmi() {
    loading.value = true;
    error.value = null;

    try {
      acmiList.value = await api.aCMI.get();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to fetch ACMI data';
      console.error('Error fetching ACMI data:', err);
    } finally {
      loading.value = false;
    }
  }

  function clearAcmi() {
    acmiList.value = [];
    error.value = null;
  }

  function clearError() {
    error.value = null;
  }

  async function addAcmi(acmiCreateRequest: ACMICreateRequest) {
    loading.value = true;
    error.value = null;

    try {
      const newAcmi = await api.aCMI.post(acmiCreateRequest);
      // Refresh the ACMI list after adding a new ACMI
      await fetchAcmi();
      return newAcmi;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to add ACMI';
      console.error('Error adding ACMI:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  async function updateAcmi(acmiUpdateRequest: ACMIUpdateRequest) {
    loading.value = true;
    error.value = null;

    try {
      await api.aCMI.put(acmiUpdateRequest);
      // Refresh the ACMI list after updating an ACMI
      await fetchAcmi();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to update ACMI';
      console.error('Error updating ACMI:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  return {
    // State
    acmiList,
    loading,
    error,

    // Getters
    acmiCount,
    hasAcmi,
    isLoading,
    hasError,

    // Actions
    fetchAcmi,
    clearAcmi,
    clearError,
    addAcmi,
    updateAcmi
  };
});
